import store from './store';
/** Reach the wasm OlmMachine on RustCrypto (private in TS, present at runtime). */
const getRustOlmMachine = (crypto) => {
    const machine = crypto.olmMachine;
    if (!machine) {
        console.warn('[KeySharing] Could not access rust-crypto OlmMachine');
        return null;
    }
    return machine;
};
/**
 * Enable room-key forwarding so this device responds to m.room_key_request
 * from the user's other sessions. Automatic outgoing requests stay disabled
 * in matrix-js-sdk; we send those manually via keyRequest.ts instead.
 */
const enableRoomKeyGossip = (crypto) => {
    const olmMachine = getRustOlmMachine(crypto);
    if (!olmMachine)
        return;
    if (olmMachine.roomKeyForwardingEnabled === false) {
        olmMachine.roomKeyForwardingEnabled = true;
        console.info('[KeySharing] Enabled room key forwarding');
    }
    console.info('[KeySharing] Gossip flags:', {
        roomKeyForwardingEnabled: olmMachine.roomKeyForwardingEnabled,
        roomKeyRequestsEnabled: olmMachine.roomKeyRequestsEnabled,
    });
};
/**
 * Ensure the current device is signed by our self-signing key.
 * bootstrapCrossSigning signs the device when it *imports* keys from SSSS,
 * but NOT when keys were already cached locally.  This catches that gap.
 */
const ensureDeviceCrossSigned = async (client, crypto) => {
    const csStatus = await crypto.getCrossSigningStatus();
    if (!csStatus.privateKeysCachedLocally.selfSigningKey) {
        console.info('[KeySharing] Self-signing key not available locally, skipping device signing (will retry after backup restore)');
        return;
    }
    const deviceId = client.getDeviceId();
    if (!deviceId)
        return;
    const verificationStatus = await crypto.getDeviceVerificationStatus(client.getUserId(), deviceId);
    if (verificationStatus?.crossSigningVerified) {
        console.info('[KeySharing] Current device already cross-signed');
        return;
    }
    console.info('[KeySharing] Current device not yet cross-signed, signing now...');
    await crypto.crossSignDevice(deviceId);
    console.info('[KeySharing] Current device cross-signed successfully');
};
/**
 * Build a UIA callback for `device_signing/upload`.
 *
 * 1. First try with an empty auth dict (works when the server accepts
 *    access-token-authenticated requests without extra UIA).
 * 2. If the server returns 401 with a UIA session, retry with
 *    `m.login.password` using the backup passphrase (which equals the
 *    account password in most Staffice deployments).
 * 3. If no passphrase is available, re-throw the error so the caller
 *    can handle it gracefully.
 */
const makeUiaCallback = (client) => async (makeRequest) => {
    try {
        await makeRequest({});
    }
    catch (uiaError) {
        const matrixErr = uiaError;
        const session = matrixErr?.data?.session;
        const passphrase = store.phrase;
        if (!session || !passphrase)
            throw uiaError;
        console.info('[KeySharing] UIA required for device_signing/upload — retrying with password auth');
        await makeRequest({
            type: 'm.login.password',
            identifier: { type: 'm.id.user', user: client.getUserId() },
            password: passphrase,
            session,
        });
    }
};
/**
 * Try a normal cross-signing bootstrap.  If it fails because the SSSS
 * private keys no longer match the public keys on the server (e.g. a
 * previous session created new keys without the passphrase), reset
 * cross-signing entirely so the account is usable again.
 */
const bootstrapOrReset = async (client, crypto) => {
    const authUpload = makeUiaCallback(client);
    try {
        await crypto.bootstrapCrossSigning({ authUploadDeviceSigningKeys: authUpload });
        console.info('[KeySharing] Cross-signing bootstrapped successfully');
    }
    catch (firstErr) {
        const msg = firstErr instanceof Error ? firstErr.message : String(firstErr);
        if (!msg.includes("public key") || !msg.includes("doesn't match")) {
            throw firstErr; // unrelated error — let the caller handle it
        }
        // Keys are corrupted (SSSS private ≠ server public).
        // The only recovery is to reset cross-signing entirely.
        console.warn('[KeySharing] Cross-signing key mismatch detected — resetting cross-signing keys');
        await crypto.bootstrapCrossSigning({
            setupNewCrossSigning: true,
            authUploadDeviceSigningKeys: authUpload,
        });
        console.info('[KeySharing] Cross-signing reset and bootstrapped successfully');
    }
};
/**
 * Bootstrap cross-signing (best-effort) and enable key backup.
 * Must be called after initRustCrypto() + startClient().
 */
export const initKeySharing = async () => {
    const client = store.client;
    const crypto = client.getCrypto();
    if (!crypto) {
        console.warn('[KeySharing] Crypto not initialized');
        return;
    }
    console.info('[KeySharing] Crypto version:', crypto.getVersion());
    // Enable forwarding so this device answers m.room_key_request from our other sessions.
    enableRoomKeyGossip(crypto);
    // 1. Enable server-side key backup so historical keys can be restored.
    try {
        await crypto.checkKeyBackupAndEnable();
        console.info('[KeySharing] Key backup checked and enabled');
    }
    catch (e) {
        console.warn('[KeySharing] Key backup check failed:', e);
    }
    // 2. Bootstrap cross-signing keys (best-effort).
    //    IMPORTANT: If private keys already exist in SSSS but we don't have
    //    the passphrase, we MUST skip bootstrap. Otherwise the SDK will fail
    //    to import from SSSS and fall through to creating NEW keys, which
    //    overwrites the existing public keys on the server — making the SSSS
    //    private keys permanently mis-matched.
    try {
        const csStatus = await crypto.getCrossSigningStatus();
        const hasKeysInSSSS = csStatus.privateKeysInSecretStorage;
        const hasPassphrase = Boolean(store.phrase);
        if (hasKeysInSSSS && !hasPassphrase) {
            console.info('[KeySharing] Cross-signing keys exist in SSSS but passphrase not available — skipping bootstrap to avoid key mismatch');
        }
        else {
            await bootstrapOrReset(client, crypto);
        }
    }
    catch (e) {
        console.warn('[KeySharing] Cross-signing bootstrap failed:', e);
    }
    // 3. Ensure the current device is cross-signed (catches gap when keys
    //    were already cached but the device signature was never uploaded).
    try {
        await ensureDeviceCrossSigned(client, crypto);
    }
    catch (e) {
        console.warn('[KeySharing] Failed to cross-sign current device:', e);
    }
    // 4. Log final status for debugging
    try {
        const status = await crypto.getCrossSigningStatus();
        console.info('[KeySharing] Cross-signing status:', JSON.stringify(status));
    }
    catch {
        // status logging is best-effort
    }
};
export default {
    initKeySharing,
};
